/**
 * SidescrollerScene.tsx — WEB.4.SS.1 / WEB.4.SS.3
 *
 * Single-tile modal sidescroller. Renders when turnPhase === "landed".
 * HexBoard remains the primary view during idle; this overlays it when active.
 *
 * Phase machine (ref-based, no per-frame Zustand writes):
 *   warp-in     → 0.4s white-flash CSS animation on mount
 *   walking-in  → player auto-walks from x=-PLAYER_SIZE to x=315 over ~1s (RAF)
 *   active      → WASD A/D + Spacebar jump enabled; NPC proximity check
 *   walking-out → player auto-walks from current x to TILE_WIDTH+PLAYER_SIZE over ~1s
 *   warp-out    → 0.4s fade + zoom-out CSS animation → calls endTurn()
 *
 * NPC proximity: within 100px → "Press E" hint. E key or NPC click → interact.
 * Interact is gated once per landing (interactedRef).
 *
 * Jump: spacebar → jumpVy=-8 initial, gravity=0.5/frame; land at y>=0.
 *
 * VISIBLE WHEN: turnPhase === "landed" AND useSidescrollerScene === true
 * TEST PATH: roll dice → player lands on any tile → SidescrollerScene renders.
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import tilePositionsData from "../../../../../data/board/tile-positions.json";
import { bgmKeyForEraRound, bgmKeyForTerrain, crossfadeBGM } from "../../audio/audio.js";
import {
  SCOUT_TERRAIN_TYPES,
  SEAT_COLORS,
  type SeatIndex,
  useGameStore,
} from "../../store/game-store.js";
import { QuizModal } from "../QuizModal/index.js";
import { ScoutModal } from "../ScoutModal/index.js";
import styles from "./SidescrollerScene.module.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const TILE_WIDTH = 900; // px — matches HexBoard / App.tsx layout
export const TILE_HEIGHT = 540; // px
export const PLAYER_SIZE = 180; // px — matches .playerChar wrapper / NPC_BASE_W for visual parity
const MOVE_SPEED = 3; // px per frame
const SPAWN_CENTER_X = 315; // px — walk-in destination
// R11.1 fix #4: slower exit pacing so the leave-tile beat reads clearly.
// Walk-in stays 1.0s (entry already feels paced via warp flash).
const WALK_IN_DURATION = 1000; // ms
const WALK_OUT_DURATION = 1600; // ms — was 1000ms; user feedback "too fast".
const WARP_DURATION = 600; // ms — was 400ms; matches warp-in flash budget.
const GRAVITY = 0.5; // px/frame²
const JUMP_VY_INIT = -8; // px/frame (upward = negative)
const NPC_PROXIMITY = 100; // px — distance to show "Press E" hint

// R4 #10 (2026-05-16): NPC sprite extracts replaced via diptych regen v3 —
// 8 NPCs × 2 frames (idle + greet) at /sprites/npcs-frames/{base}-{frame}-0.png.
// Diptych approach guarantees same character in both frames. Flag flipped false
// to wire the real sprites. Greet frame swaps in for ~1.5s after interact then
// falls back to idle.
const USE_NPC_PLACEHOLDER = false;

// R5 #1+#9 (codex LOW fix): per-NPC scale + base size bump.
// Base 180×270 (was 144×216 — 1.25× bump per user "Increase the NPCs size").
// Per-NPC scale multiplier from vision-checked vs BG element proportions.
const NPC_BASE_W = 180;
const NPC_BASE_H = 270;
const NPC_SCALE: Record<string, number> = {
  "builder-e1.png": 1.0,
  // T-1: scout uses builder scale (visual is same sprite, different interaction key)
  "scout-e1.png": 1.0,
  "newsgirl-e1.png": 1.3,
  "bureaucrat-e1.png": 1.3,
  "guard.png": 1.2,
  "inmate.png": 1.2,
  "trader-e1.png": 1.2,
  "keeper.png": 1.1,
  "mayor.png": 1.3,
};

function getNpcScale(file: string): number {
  return NPC_SCALE[file] ?? 1.0;
}

// Walk-cycle bob: translateY oscillation when moving (placeholder for real walk frames)
const BOB_AMPLITUDE = 4; // px
const BOB_FREQ = 8; // cycles per second

/**
 * R18-FC-4: Per-NPC Y offset (px) for the speech bubble.
 * Positive = marginTop pushes bubble DOWN toward a shorter sprite.
 * scout-e1.png is visually shorter than builder-e1.png at the same scale —
 * adding 60px closes the gap between the bubble and the sprite head.
 */
export const NPC_DIALOG_Y_OFFSET: Record<string, number> = {
  "builder-e1.png": 0,
  "scout-e1.png": 60, // Scout sprite is shorter — bubble moved 60px closer to sprite
  "newsgirl-e1.png": 0,
  "bureaucrat-e1.png": 0,
  "guard.png": 0,
  "inmate.png": 0,
  "trader-e1.png": 0,
  "keeper.png": 0,
  "mayor.png": 0,
};

// ---------------------------------------------------------------------------
// Tile type helpers
// ---------------------------------------------------------------------------

type TileEntry = { index: number; type: string; x: number; y: number };
const _tileTypeByIndex: Record<number, string> = Object.fromEntries(
  (tilePositionsData.tiles as TileEntry[]).map((t) => [t.index, t.type]),
);

function resolveTerrainKey(tileType: string): string {
  switch (tileType) {
    case "Coast":
      return "coastline";
    case "Jail":
      return "jail";
    case "Trading":
      return "trading";
    case "Teleport":
      return "teleport";
    default:
      return tileType.toLowerCase();
  }
}

// ---------------------------------------------------------------------------
// NPC placement per tile type
// ---------------------------------------------------------------------------

type NpcSpec = { file: string; x: number /* 0-1 fraction */; label: string; testId: string };

function getNpcsForTileType(tileType: string): NpcSpec[] {
  switch (tileType) {
    case "Jail":
      return [
        { file: "guard.png", x: 0.15, label: "Guard", testId: "ss-npc-guard" },
        // R10 #2: inmate moved x:0.92→0.78 — was hidden behind HandPanel right column
        // (HandPanel 280px on 1920px ≈ 14.5% from right → 85.5% from left).
        // 0.78 = 22% from right = clear of panel. Still passes guard < inmate check.
        { file: "inmate.png", x: 0.78, label: "Inmate", testId: "ss-npc-inmate" },
      ];
    case "Trading":
      return [{ file: "trader-e1.png", x: 0.72, label: "Trader", testId: "ss-npc-trader" }];
    case "Teleport":
      return [{ file: "keeper.png", x: 0.72, label: "Keeper", testId: "ss-npc-keeper" }];
    case "Event":
      return [{ file: "newsgirl-e1.png", x: 0.88, label: "Newsgirl", testId: "ss-npc-newsgirl" }];
    case "Policy":
      return [
        // R11.2.5: bureaucrat moved LEFT (x=0.25) to mirror builder NPC layout.
        { file: "bureaucrat-e1.png", x: 0.25, label: "Bureaucrat", testId: "ss-npc-bureaucrat" },
      ];
    case "Start":
      return [{ file: "mayor.png", x: 0.72, label: "Mayor", testId: "ss-npc-mayor" }];
    default:
      // All buildable terrain tiles: Plains, Forest, Coast, River, Tundra,
      // Mountain, Desert, Volcanic, Urban.
      // R9 #5: builder NPC moved to LEFT side (x=0.25) per user request.
      // T-1: Scout NPC added at x=0.55 (center-right) on terrain tiles.
      // Uses virtual file "scout-e1.png" (resolves to builder sprite with onError fallback)
      // so interaction keys are distinct from the builder NPC key.
      if (SCOUT_TERRAIN_TYPES.has(tileType)) {
        return [
          { file: "builder-e1.png", x: 0.25, label: "Builder", testId: "ss-npc-builder" },
          { file: "scout-e1.png", x: 0.55, label: "Scout", testId: "ss-npc-scout" },
        ];
      }
      return [{ file: "builder-e1.png", x: 0.25, label: "Builder", testId: "ss-npc-builder" }];
  }
}

// NPC hover-text per file — pre-interaction line shown in speech bubble.
// R3 Bug 5: guard line is dynamic (uses computed bail) — handled via
// resolveNpcSpeech() below; this map only supplies static fallbacks.
const NPC_SPEECH: Record<string, string> = {
  "builder-e1.png": "Let's build something...",
  // T-1: scout speech bubble shows era-scaled cost dynamically via resolveNpcSpeech()
  "scout-e1.png": "Scout the area?",
  "newsgirl-e1.png": "Read all about it!",
  "bureaucrat-e1.png": "New policy on the desk",
  "guard.png": "Pay to leave?",
  "inmate.png": "Try to escape?",
  "trader-e1.png": "Care to trade?",
  "keeper.png": "Where to, traveler?",
  "mayor.png": "Welcome back! +20 tokens",
};

// R3 Bug 9 — post-interaction lines shown on re-interact for one-shot NPCs.
// Builder NPC is NOT in this map — Builder is always re-interactable
// (player may build / demolish multiple times per landing).
const NPC_COMPLETED_SPEECH: Record<string, string> = {
  "newsgirl-e1.png": "Already drew today's headline.",
  "bureaucrat-e1.png": "You already adopted this policy.",
  "guard.png": "You're free — move along.",
  "inmate.png": "Already tried escaping this turn.",
  "trader-e1.png": "We're done trading for now.",
  "keeper.png": "Already teleported once.",
  "mayor.png": "Hometown's bonus already paid.",
  // T-1: Scout one-shot per landing
  "scout-e1.png": "Already scouted this spot.",
};

// Builder NPC files that bypass the one-shot completed gate.
const ALWAYS_REINTERACTABLE = new Set(["builder-e1.png"]);

// ---------------------------------------------------------------------------
// Per-facility screen height (R4 Bug 2b)
// Values are percent of viewport height. Applied as `height: ${pct}vh` on the
// facilitySprite img, replacing the old static 380px rule.
// Verify at 1080p: tallest = wind (70%) → 756px; facility bottom at
// min groundY (8%) → 86px; top at 86+756=842px < 1080px ✓
// ---------------------------------------------------------------------------

export const FACILITY_SCREEN_HEIGHT_PCT: Record<string, number> = {
  hydro: 50,
  wind: 70,
  solar: 30,
  coal: 55,
  nuclear: 45,
  oil: 50,
  naturalgas: 45, // no sprite yet — future-proof
  geothermal: 35,
  tidal: 30,
  offshorewind: 60, // no distinct sprite; shares wind
  floatingsolar: 25,
  biomass: 40,
  offshoredrilling: 50,
  wastetoenergy: 40,
};

/** Fallback height pct for unknown facilityId */
const DEFAULT_FACILITY_HEIGHT_PCT = 35;

// ---------------------------------------------------------------------------
// Water spawn X percent per terrain (R4 Bug 4)
// Percent from left edge where water-eligible facilities should be anchored.
// Verified via vision inspection of bg PNGs:
//   bg_coastline.png — ocean occupies left ~60% of frame; beach transitions
//     at ~60%. Best open-water spawn: 25% from left (clear open sea).
//   bg_river.png — river meanders through centre; widest at lower area ~35-45%
//     from left. Best: 38% from left.
// Non-water terrains → field unused (land facilities always land-positioned).
// ---------------------------------------------------------------------------

export const WATER_FACILITY_X_PCT: Record<string, number> = {
  coastline: 25, // open sea, left third of coastline BG
  river: 38, // river centre at lower portion of river BG
};

/** Water-eligible facility ids — position these on water area of bg */
export const WATER_FACILITY_IDS = new Set([
  "floatingsolar",
  "offshoredrilling",
  "tidal",
  "offshorewind", // future — shares wind sprite
]);

/**
 * R5 #12 — Per-terrain water surface Y, percent from bottom.
 * Vision-derived from bg PNG inspection (2026-05-16):
 *   bg_coastline.png — sea/wave crest sits at ≈33% from bottom
 *     (golden beach occupies lower ~33%; wave line is where sea meets sand).
 *   bg_river.png — near bank of the river at lower-centre is ≈22% from bottom.
 * Facility BASE is anchored at this Y so it sits on the water surface.
 */
export const WATER_GROUND_Y_PCT: Record<string, number> = {
  coastline: 33, // sea surface / wave crest line
  river: 22, // near-bank water surface at lower centre
};

/** Fallback if terrain not in WATER_GROUND_Y_PCT table. */
const WATER_GROUND_Y_PCT_DEFAULT = 20;

// ---------------------------------------------------------------------------
// Ground Y percent per terrain key
// PT bug 1 fix: drop the platform overlay (fgDeco) which clashed with
// detailed landscape bgs. Instead, each bg has a baked-in ground at a
// specific visual height. GROUND_Y_PERCENT[key] = distance from bottom
// where the ground surface appears (percent of scene height).
// ---------------------------------------------------------------------------

export const GROUND_Y_PERCENT: Record<string, number> = {
  plains: 20,
  coastline: 28,
  desert: 25,
  forest: 12,
  mountain: 12,
  river: 10,
  // R13 #11: tundra vision-recheck — snowy foreground surface top at ~22% from bottom.
  // Was 30% — caused NPC + player to float above the snow bank.
  // Image layers from bottom: dark soil (~8%) → white snow foreground → ice lake.
  // Top of white snow foreground ≈ 22% from bottom = correct foot anchor.
  tundra: 22,
  urban: 18,
  volcanic: 18,
  jail: 8,
  trading: 15,
  // R10 #12 / R13 #11: teleport BG vision-checked — golden rocky ground plane top at ~12% from bottom.
  // Was 18% (R10 fix) — still caused NPC + player to float above the stone ground.
  // Ground band occupies bottom ~12% of image; top edge ≈ 12% from bottom.
  teleport: 12,
  event: 15,
  policy: 10,
  start: 20,
};

export function groundYForTerrain(terrainKey: string): number {
  return GROUND_Y_PERCENT[terrainKey] ?? 20;
}

// ---------------------------------------------------------------------------
// Sky gradient per era
// ---------------------------------------------------------------------------

function skyGradient(era: 1 | 2 | 3 | 4, isJail: boolean): string {
  if (isJail) {
    return "linear-gradient(180deg, #1A1A2A 0%, #2A2A40 30%, #3A3055 60%, #4A3870 80%, #5A4888 100%)";
  }
  switch (era) {
    case 1:
      return "linear-gradient(180deg, #4A3010 0%, #7A5020 20%, #C87830 55%, #E8A050 80%, #F0C070 100%)";
    case 2:
      return "linear-gradient(180deg, #3A3820 0%, #6A6030 25%, #B09040 55%, #D0B060 80%, #E8C880 100%)";
    case 3:
      return "linear-gradient(180deg, #203040 0%, #406080 25%, #6090B0 55%, #80B0D0 80%, #A0D0E8 100%)";
    default:
      return "linear-gradient(180deg, #102040 0%, #204878 30%, #3070A8 60%, #50A0D0 85%, #70C0F0 100%)";
  }
}

// ---------------------------------------------------------------------------
// Held-keys module set — lives outside component to avoid closure stale issues
// ---------------------------------------------------------------------------

const _heldKeys = new Set<string>();

// Phase machine type
type ScenePhase = "warp-in" | "walking-in" | "active" | "walking-out" | "warp-out";

// ---------------------------------------------------------------------------
// SidescrollerScene — public component
// ---------------------------------------------------------------------------

/** Feature flag — set false to revert to TileStage. */
export const USE_SIDESCROLLER_SCENE = true;

export function SidescrollerScene(): React.ReactElement | null {
  const turnPhase = useGameStore((s) => s.turnPhase);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const playerPerks = useGameStore((s) => s.playerPerks);
  const era = useGameStore((s) => s.era);
  const scenePos = useGameStore((s) => s.scenePos);
  const setScenePos = useGameStore((s) => s.setScenePos);
  // WEB.4.SS.5: Leave Tile button increments this — trigger walk-out animation
  const sceneLeaveRequestId = useGameStore((s) => s.sceneLeaveRequestId);

  // Walk-cycle anim state: frame counter for bob
  const [walkTime, setWalkTime] = useState(0);
  // Direction: 1 = right (default), -1 = left
  const dirRef = useRef<1 | -1>(1);
  // RAF handle (movement)
  const rafRef = useRef<number | null>(null);
  // Timestamp for last frame (for frame-rate-independent bob)
  const lastTimeRef = useRef<number>(0);

  // codex R1 HIGH fix: compute tileType + npcs BEFORE early return so hook
  // order is stable.
  const tileIdx = playerTiles[currentSeat] ?? 0;
  const tileType = _tileTypeByIndex[tileIdx] ?? "Plains";
  const npcs = useMemo(() => getNpcsForTileType(tileType), [tileType]);
  // Facility built on the current tile (undefined if none). Read before early return
  // so hook order is stable.
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const builtFacilityId = builtFacilities[tileIdx];
  // R5 #6: check if anim already played for this tile so inner component skips
  // the construction sequence on subsequent landings.
  const facilityBuildAnimPlayedTiles = useGameStore((s) => s.facilityBuildAnimPlayedTiles);

  // WEB.4.SS.5.PT2: 1000ms board-token-animation delay before the scene mounts.
  // R11.1 fix #1 — gate scene mount on the live boardAnimationActive store
  // flag instead of a fixed 1000ms timer. HexBoard sets the flag for the
  // duration of the tile-by-tile walk; we only mount AFTER the walk finishes.
  //
  // Initial mount with landed (tests + page reload mid-game) still skips the
  // wait — only "real" idle→landed transitions trigger the gate.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const prevTurnPhaseRef = useRef(turnPhase);
  const [hadFreshTransition, setHadFreshTransition] = useState(false);
  useEffect(() => {
    const prev = prevTurnPhaseRef.current;
    prevTurnPhaseRef.current = turnPhase;
    if (turnPhase !== "landed") {
      setHadFreshTransition(false);
      return;
    }
    if (prev === "idle") setHadFreshTransition(true);
  }, [turnPhase]);
  // Ready when: (a) not a fresh transition (test / page reload), OR
  // (b) fresh transition AND walk has finished (flag flipped back to false).
  const boardAnimDone = !hadFreshTransition || !boardAnimationActive;

  // Visibility gate (after hooks — all hooks called above)
  if (turnPhase !== "landed") return null;
  if (!boardAnimDone) return null;

  const isJail = tileType === "Jail";
  const seatColor = SEAT_COLORS[currentSeat as SeatIndex];

  // Remount key — resets position on new tile/seat via key= on wrapper
  const sceneKey = `ss-${tileIdx}-${currentSeat}`;

  return (
    <SidescrollerSceneInner
      key={sceneKey}
      tileType={tileType}
      isJail={isJail}
      npcs={npcs}
      era={era}
      seatColor={seatColor}
      currentSeat={currentSeat}
      archetype={playerPerks[currentSeat] ?? "engineer"}
      scenePos={scenePos}
      setScenePos={setScenePos}
      walkTime={walkTime}
      setWalkTime={setWalkTime}
      dirRef={dirRef}
      rafRef={rafRef}
      lastTimeRef={lastTimeRef}
      sceneLeaveRequestId={sceneLeaveRequestId}
      builtFacilityId={builtFacilityId}
      facilityBuildAnimAlreadyPlayed={
        // R5 #6: skip anim when tile already had a facility before this landing.
        // facilityBuildAnimPlayedTiles is marked atomically with buildFacility.
        // When another player lands on the SAME tile after a previous build, the
        // inner component remounts (key={sceneKey} changes on currentSeat change)
        // with builtFacilityId already defined AND set.has(tileIdx) true → skip.
        // The building player sees the anim: at mount time builtFacilityId is
        // undefined (build hasn't happened yet) → false. After build, prop
        // re-evaluates to true but buildAnimSkipRef (frozen at mount) stays false.
        builtFacilityId !== undefined && facilityBuildAnimPlayedTiles.has(tileIdx)
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Inner component — receives key from parent so remount resets RAF state
// ---------------------------------------------------------------------------

type PlayerArchetype = "engineer" | "scientist" | "activist" | "politician";

interface InnerProps {
  tileType: string;
  isJail: boolean;
  npcs: NpcSpec[];
  era: 1 | 2 | 3 | 4;
  seatColor: string;
  currentSeat: number;
  /** R23-GH: archetype for this seat — drives sprite selection */
  archetype: PlayerArchetype;
  scenePos: { x: number; y: number };
  setScenePos: (x: number, y: number) => void;
  walkTime: number;
  setWalkTime: React.Dispatch<React.SetStateAction<number>>;
  dirRef: React.MutableRefObject<1 | -1>;
  rafRef: React.MutableRefObject<number | null>;
  lastTimeRef: React.MutableRefObject<number>;
  /** Counter from store — increments on Leave Tile click. */
  sceneLeaveRequestId: number;
  /** facilityId built on this tile (e.g. "coal", "solar"), or undefined */
  builtFacilityId?: string | undefined;
  /**
   * R5 #6 — true when the build animation for this tile already fired in this
   * game session. When true, render the facility in settled state (skip
   * dust/scaffold/reveal CSS keyframes).
   */
  facilityBuildAnimAlreadyPlayed: boolean;
}

function SidescrollerSceneInner({
  tileType,
  isJail,
  npcs,
  era,
  seatColor,
  currentSeat,
  archetype,
  scenePos,
  setScenePos,
  walkTime,
  setWalkTime,
  dirRef,
  rafRef,
  lastTimeRef,
  sceneLeaveRequestId,
  builtFacilityId,
  facilityBuildAnimAlreadyPlayed,
}: InnerProps): React.ReactElement {
  // R5 #6: capture whether the build anim should be skipped AT MOUNT TIME.
  // Using a ref means this value is frozen at first render and never re-read from
  // the prop on subsequent re-renders. This correctly gates the anim:
  //   - Another player lands on a pre-built tile → prop=true at mount → skip anim
  //   - Building player is here and then builds → prop=false at mount → anim fires
  //     (even though the prop becomes true on the re-render after build)
  const buildAnimSkipRef = useRef(facilityBuildAnimAlreadyPlayed);

  // R23-GH: animTime tracks wall-clock ms for sprite frame cycling (250ms/frame).
  const [animTime, setAnimTime] = useState(0);
  const animTimeRef = useRef(0);

  // R23-GH: preload all 5 frames for the active archetype to avoid first-frame pop.
  useEffect(() => {
    const urls = [
      `/sprites/players/${archetype}-portrait.png`,
      `/sprites/players/${archetype}-idle-0.png`,
      `/sprites/players/${archetype}-idle-1.png`,
      `/sprites/players/${archetype}-walk-0.png`,
      `/sprites/players/${archetype}-walk-1.png`,
    ];
    for (const url of urls) {
      const img = new Image();
      img.src = url;
    }
  }, [archetype]); // re-preload if archetype changes (shouldn't in practice)

  // ---- Phase machine ----
  const phaseRef = useRef<ScenePhase>("warp-in");
  const [phase, setPhase] = useState<ScenePhase>("warp-in");

  // Jump state (ref-based, no re-render per frame)
  const jumpVyRef = useRef(0);
  const jumpYRef = useRef(0);
  const [jumpY, setJumpY] = useState(0); // React state for rendering only

  // NPC proximity hint
  const [proximateNpc, setProximateNpc] = useState<string | null>(null); // testId of near NPC
  // R4 #10 + R5 #1: track which NPC is currently in "greet" frame.
  // R5 #1 (codex MED fix): greet now stays until the interaction modal closes —
  // NOT a fixed timer. Cleared by the modal-union effect below when every
  // blocking surface is closed.
  const [greetingNpcFile, setGreetingNpcFile] = useState<string | null>(null);
  const triggerGreet = useCallback((npcFile: string) => {
    setGreetingNpcFile(npcFile);
  }, []);
  // Modal-union: greet clears when ALL interaction surfaces are closed.
  const prisonGuardModalOpen = useGameStore((s) => s.prisonGuardModalOpen);
  const inmateEscapeModalOpen = useGameStore((s) => s.inmateEscapeModalOpen);
  const buildMenuOpen = useGameStore((s) => s.buildMenuOpen);
  const tradingPromptOpen = useGameStore((s) => s.tradingPromptOpen);
  const pendingDrawCard = useGameStore((s) => s.pendingDrawCard);
  const pendingFreeDrawCard = useGameStore((s) => s.pendingFreeDrawCard);
  const pendingEffectAck = useGameStore((s) => s.pendingEffectAck);
  const pendingTollPayment = useGameStore((s) => s.pendingTollPayment);
  const detailModalCardForGreet = useGameStore((s) => s.detailModalCard);
  const tilePickerOpen = useGameStore((s) => s.tilePickerOpen);
  const targetPickerCard = useGameStore((s) => s.targetPickerCard);
  const handFullPendingCard = useGameStore((s) => s.handFullPendingCard);
  const takeoverConfirmOpen = useGameStore((s) => s.takeoverConfirmOpen);
  const pendingQuizForGreet = useGameStore((s) => s.pendingQuiz);
  useEffect(() => {
    const anyOpen =
      prisonGuardModalOpen ||
      inmateEscapeModalOpen ||
      buildMenuOpen ||
      tradingPromptOpen ||
      pendingDrawCard !== null ||
      pendingFreeDrawCard !== null ||
      pendingEffectAck !== null ||
      pendingTollPayment !== null ||
      detailModalCardForGreet !== null ||
      tilePickerOpen ||
      targetPickerCard !== null ||
      handFullPendingCard !== null ||
      takeoverConfirmOpen ||
      pendingQuizForGreet !== null;
    if (!anyOpen && greetingNpcFile !== null) {
      setGreetingNpcFile(null);
    }
  }, [
    prisonGuardModalOpen,
    inmateEscapeModalOpen,
    buildMenuOpen,
    tradingPromptOpen,
    pendingDrawCard,
    pendingFreeDrawCard,
    pendingEffectAck,
    pendingTollPayment,
    detailModalCardForGreet,
    tilePickerOpen,
    targetPickerCard,
    handFullPendingCard,
    takeoverConfirmOpen,
    pendingQuizForGreet,
    greetingNpcFile,
  ]);
  // R3 Bug 9: per-NPC interaction state replaces the old global one-shot gate.
  // Builder NPC bypasses the gate (ALWAYS_REINTERACTABLE set above).
  const npcInteractionState = useGameStore((s) => s.npcInteractionState);
  const getNpcInteractionKey = useGameStore((s) => s.getNpcInteractionKey);
  const resetNpcInteractionsForTile = useGameStore((s) => s.resetNpcInteractionsForTile);
  const playerTilesLive = useGameStore((s) => s.playerTiles);
  const currentTileIdxLive = playerTilesLive[currentSeat] ?? 0;
  // Walk-out timeout
  const warpOutTimerRef = useRef<number | null>(null);

  const clamp = useCallback(
    (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v)),
    [],
  );

  // ---- WEB.4.SS.5: Leave-Tile request → walking-out phase ----
  // PrimaryActionZone increments sceneLeaveRequestId on Leave Tile click.
  // When request changes AND we're in "active" phase, kick off walk-out.
  // RAF in the movement loop will detect phase === "walking-out" and auto-walk
  // the player to the right edge, then advance to warp-out → endTurn.
  useEffect(() => {
    if (sceneLeaveRequestId > 0 && phaseRef.current === "active") {
      phaseRef.current = "walking-out";
      setPhase("walking-out");
    }
  }, [sceneLeaveRequestId]);

  // ---- Bug 3: reset NPC interaction state on every new tile landing ----
  // This component remounts via key={sceneKey} on parent for each new landing,
  // so an empty deps array is correct: runs once per remount = once per landing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only reset — intentional
  useEffect(() => {
    resetNpcInteractionsForTile(currentTileIdxLive);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Keyboard listeners ----
  useEffect(() => {
    const MOVEMENT_KEYS = new Set(["a", "d", "A", "D", "ArrowLeft", "ArrowRight"]);

    function normalizeKey(k: string): string {
      if (k === "ArrowLeft") return "a";
      if (k === "ArrowRight") return "d";
      return k.toLowerCase();
    }

    function onKeyDown(e: KeyboardEvent): void {
      // WEB.4.PM (P0.2): pause gates gameplay keyboard. Esc handler lives in
      // PauseMenu — don't intercept it here. While paused, also flush held
      // keys so the player doesn't keep walking after Resume.
      if (useGameStore.getState().paused) {
        _heldKeys.clear();
        return;
      }
      if (MOVEMENT_KEYS.has(e.key)) {
        _heldKeys.add(normalizeKey(e.key));
      }
      // Spacebar jump — only in active phase, only when on ground
      if (
        e.key === " " &&
        phaseRef.current === "active" &&
        jumpYRef.current >= 0 &&
        jumpVyRef.current === 0
      ) {
        e.preventDefault();
        jumpVyRef.current = JUMP_VY_INIT;
      }
      // E key — interact with nearest NPC if proximity.
      // R3 Bug 9: per-NPC gate via npcInteractionState (not the old
      // global interactedRef). Builder NPC always re-interactable.
      if ((e.key === "e" || e.key === "E") && phaseRef.current === "active") {
        const state = useGameStore.getState();
        const playerX = state.scenePos.x + PLAYER_SIZE / 2;
        const tileIdxNow = state.playerTiles[state.currentSeat] ?? 0;
        for (const npc of npcs) {
          const npcAbsX = npc.x * TILE_WIDTH;
          if (Math.abs(playerX - npcAbsX) < NPC_PROXIMITY) {
            const isBuilder = ALWAYS_REINTERACTABLE.has(npc.file);
            const key = state.getNpcInteractionKey(tileIdxNow, npc.file);
            const alreadyDone = state.npcInteractionState[key] === "completed";
            if (alreadyDone && !isBuilder) {
              // re-interact on completed one-shot NPC: speech bubble
              // already shows the post line; swallow the press silently.
              break;
            }
            triggerGreet(npc.file);
            triggerNpcInteract(npc);
            // R6 #1: do NOT mark NPC interacted here — only on action completion.
            // markNpcInteracted is now called inside store actions
            // (payToLeaveJail, attemptEscapeJail, executeTrade, confirmTilePick
            // Teleport, free-card take/discard, etc.) so cancelling a modal
            // without action leaves the NPC re-interactable.
            break;
          }
        }
      }
    }

    function onKeyUp(e: KeyboardEvent): void {
      _heldKeys.delete(normalizeKey(e.key));
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      _heldKeys.clear();
    };
  }, [npcs, triggerGreet]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- NPC interact handler ----
  const triggerNpcInteract = useCallback((npc: NpcSpec) => {
    const s = useGameStore.getState();
    switch (npc.file) {
      case "builder-e1.png":
        // Bug 4 fix: BuildMenu opens ONLY via E-interact with the Builder NPC.
        // rollDice no longer auto-opens it.
        s.openBuildMenu();
        break;
      case "newsgirl-e1.png": {
        // M-1 SDG Quiz: offer quiz BEFORE the normal card draw.
        // offerQuiz() is a no-op if: already completed this round, or quiz pending.
        // When pendingQuiz becomes non-null, QuizModal mounts.
        // After quiz resolve/skip, QuizModal unmounts and player can interact again
        // to draw their card (NPC state NOT marked completed until card drawn).
        const alreadyQuizzed = s.quizCompletedThisRound[String(s.currentSeat as SeatIndex)];
        const quizPending = s.pendingQuiz !== null;
        if (!alreadyQuizzed && !quizPending) {
          s.offerQuiz();
          // offerQuiz sets pendingQuiz → QuizModal mounts.
          // Don't proceed to card draw yet; player must resolve quiz first.
          break;
        }
        // WEB.4.R2.CF: full card-flow orchestration (A/B/C/D per spec).
        // handleNpcCardDraw branches on isImmediate / buyCost / handFull internally.
        s.handleNpcCardDraw(s.currentSeat as SeatIndex, "event");
        break;
      }
      case "bureaucrat-e1.png": {
        // T-4 (R16): 10% chance per landing (era >= 3) — tax precedes auction.
        // If tax fires the modal opens; suppress the auction + normal draw for this landing.
        const taxFired = s.triggerPollutionTaxIfRolled(s.currentSeat as SeatIndex);
        if (taxFired) break;
        // T-2: every 3rd policy-tile landing triggers an auction instead of a normal draw.
        // triggerAuctionIfDue increments the per-seat counter and returns true when an
        // auction fires. When it returns false, fall through to the normal draw path.
        const auctionFired = s.triggerAuctionIfDue(s.currentSeat as SeatIndex);
        if (!auctionFired) {
          // WEB.4.R2.CF: full card-flow orchestration (A/B/C/D per spec).
          s.handleNpcCardDraw(s.currentSeat as SeatIndex, "policy");
        }
        break;
      }
      case "guard.png":
        // R4: open dramatic Prison Guard modal instead of silent payToLeaveJail()
        s.openPrisonGuardModal();
        break;
      case "inmate.png":
        // R4: open dramatic Inmate Escape modal instead of silent attemptEscapeJail()
        s.openInmateEscapeModal();
        break;
      case "trader-e1.png":
        // Trading: open trading prompt (no openTradingPrompt action; set directly)
        useGameStore.setState({ tradingPromptOpen: true });
        break;
      case "keeper.png":
        // Teleport: open tile picker (same params as rollDice Teleport landing)
        s.openTilePicker({
          filter: { kind: "Teleport" },
          mode: "teleport",
          cardId: null,
          intent: { kind: "Teleport" },
        });
        break;
      case "mayor.png":
        // R10 #11: landing on Start tile → Mayor grants +20 tokens on E-interact.
        // Pass-Start bonus is separate (+20 in rollDice). Mayor marks NPC completed.
        s.claimStartBonus();
        break;
      case "scout-e1.png":
        // T-1: Scout NPC on terrain tiles — offer the scout mini-game.
        // offerScout() gates internally: boardAnimationActive, input blocked,
        // already scouted this landing, pendingScout active.
        s.offerScout();
        break;
      default:
        break;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Warp-in → walking-in transition ----
  useEffect(() => {
    // Phase starts as "warp-in"; after WARP_DURATION, start walking-in.
    // setScenePos is stable (Zustand action) — safe to omit from deps.
    const tid = window.setTimeout(() => {
      phaseRef.current = "walking-in";
      setPhase("walking-in");
      // Seed store position at left edge
      useGameStore.getState().setScenePos(-PLAYER_SIZE, 0);
    }, WARP_DURATION);
    return () => window.clearTimeout(tid);
  }, []); // mount-only: warp-in fires once per remount

  // ---- RAF main loop ----
  useEffect(() => {
    // Walk-in start time
    let walkInStartTs: number | null = null;
    let walkInStartX = -PLAYER_SIZE;

    function frame(ts: number): void {
      const dt = lastTimeRef.current === 0 ? 16.67 : ts - lastTimeRef.current;
      lastTimeRef.current = ts;
      const currentPhase = phaseRef.current;

      // R23-GH: tick wall-clock animTime (ms) for sprite frame cycling.
      animTimeRef.current += dt;
      setAnimTime(animTimeRef.current);

      // ---- Walking-in ----
      if (currentPhase === "walking-in") {
        if (walkInStartTs === null) {
          walkInStartTs = ts;
          walkInStartX = useGameStore.getState().scenePos.x;
        }
        const elapsed = ts - walkInStartTs;
        const t = Math.min(elapsed / WALK_IN_DURATION, 1);
        const nx = walkInStartX + (SPAWN_CENTER_X - walkInStartX) * t;
        setScenePos(nx, 0);
        dirRef.current = 1;
        setWalkTime((wt) => wt + dt / 1000);

        if (t >= 1) {
          phaseRef.current = "active";
          setPhase("active");
          setScenePos(SPAWN_CENTER_X, 0);
          setWalkTime(0);
        }
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      // ---- Walking-out ----
      if (currentPhase === "walking-out") {
        const currentX = useGameStore.getState().scenePos.x;
        // WEB.4.SS.5.PT (playtest fix): scene is 100vw, not 900px. TILE_WIDTH+64
        // stopped player mid-screen. Walk off the actual viewport edge.
        const viewportW = typeof window !== "undefined" ? window.innerWidth : 1920;
        const target = viewportW + PLAYER_SIZE;
        const speed = (target - currentX) / (WALK_OUT_DURATION / 16.67);
        const nx = currentX + Math.max(speed, MOVE_SPEED * 4);
        setScenePos(nx, 0);
        dirRef.current = 1;
        setWalkTime((wt) => wt + dt / 1000);

        if (nx >= target) {
          setScenePos(target, 0);
          setWalkTime(0);
          phaseRef.current = "warp-out";
          setPhase("warp-out");
          // After warp-out animation, call endTurn
          warpOutTimerRef.current = window.setTimeout(() => {
            useGameStore.getState().endTurn();
          }, WARP_DURATION);
        }
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      // ---- Active phase ----
      if (currentPhase === "active") {
        // WEB.4.PM (P0.2): pause freezes player movement + NPC proximity.
        // _heldKeys may still hold keys pressed BEFORE pause was opened
        // (Esc bypasses our keydown), so reading store every frame is the
        // only reliable gate. RAF keeps re-arming so resume is instant.
        if (useGameStore.getState().paused) {
          rafRef.current = requestAnimationFrame(frame);
          return;
        }
        const movingLeft = _heldKeys.has("a");
        const movingRight = _heldKeys.has("d");
        const anyMove = movingLeft || movingRight;

        if (anyMove) {
          if (movingLeft) dirRef.current = -1;
          else if (movingRight) dirRef.current = 1;
          setWalkTime((wt) => wt + dt / 1000);
        } else {
          setWalkTime(0);
        }

        const currentX = useGameStore.getState().scenePos.x;
        let nx = currentX;
        if (movingLeft) nx -= MOVE_SPEED;
        if (movingRight) nx += MOVE_SPEED;
        // WEB.4.SS.5.PT2 (playtest fix): scene is 100vw, not 900px. Clamp to
        // viewport width so player can traverse the FULL visible screen.
        const viewportW = typeof window !== "undefined" ? window.innerWidth : 1920;
        nx = clamp(nx, 0, viewportW - PLAYER_SIZE);

        // Jump physics
        let newJumpY = jumpYRef.current;
        if (jumpVyRef.current !== 0 || newJumpY < 0) {
          jumpVyRef.current += GRAVITY;
          newJumpY = jumpYRef.current + jumpVyRef.current;
          if (newJumpY >= 0) {
            newJumpY = 0;
            jumpVyRef.current = 0;
          }
          jumpYRef.current = newJumpY;
          setJumpY(newJumpY);
        }

        if (anyMove || nx !== currentX) {
          setScenePos(nx, 0);
        }

        // NPC proximity check (per frame, cheap abs comparison)
        const playerCenter = nx + PLAYER_SIZE / 2;
        let nearNpc: string | null = null;
        for (const npc of npcs) {
          const npcAbsX = npc.x * TILE_WIDTH;
          if (Math.abs(playerCenter - npcAbsX) < NPC_PROXIMITY) {
            nearNpc = npc.testId;
            break;
          }
        }
        setProximateNpc(nearNpc);
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimeRef.current = 0;
    };
  }, [clamp, dirRef, lastTimeRef, npcs, rafRef, setScenePos, setWalkTime]);

  // ---- Walk-out cleanup ----
  useEffect(() => {
    return () => {
      if (warpOutTimerRef.current !== null) {
        window.clearTimeout(warpOutTimerRef.current);
        warpOutTimerRef.current = null;
      }
    };
  }, []);

  // ---- Handle "Leave Tile" — PrimaryActionZone already calls endTurn directly.
  // We intercept by watching for walk-out trigger from a "leave" button inside scene.
  // The existing PrimaryActionZone "Leave Tile" button calls endTurn() directly.
  // For the walk-out animation: we expose a triggerWalkOut function via a data attribute
  // so tests can trigger it. In practice, user presses PrimaryActionZone "Leave Tile"
  // which calls endTurn() — which is acceptable (no walk-out animation for that path).
  // Phase 3 TODO: intercept PrimaryActionZone endTurn call to play walk-out first.

  // ---- Bob calculation ----
  const isMoving =
    (phase === "active" || phase === "walking-in" || phase === "walking-out") &&
    (_heldKeys.has("a") || _heldKeys.has("d") || phase === "walking-in" || phase === "walking-out");
  const bobY = isMoving ? Math.sin(walkTime * BOB_FREQ * Math.PI * 2) * BOB_AMPLITUDE : 0;

  const currentX = scenePos.x;
  // R7 #9: render player at %-of-viewport so position scales with viewport
  // resize (e.g. split-screen). currentX stays in 0-TILE_WIDTH logical px for
  // RAF + proximity math; rendered position = (currentX / TILE_WIDTH) * 100%.
  const playerLeftPct = `${(currentX / TILE_WIDTH) * 100}%`;
  const playerTransform = `translate(0, ${bobY + jumpY}px) scaleX(${dirRef.current})`;

  const skyStyle: React.CSSProperties = {
    background: skyGradient(era, isJail),
  };

  const terrainKey = resolveTerrainKey(tileType);
  const bgSrc = `/images/terrain/backgrounds/bg_${terrainKey}.jpg`;
  const groundY = groundYForTerrain(terrainKey);

  // R4 #8: terrain BGM crossfade on tile entry.
  // R11.2.5 fix #9: Inner only renders AFTER the wrapper passes boardAnimDone,
  // so by the time this effect runs the token has already arrived. The effect
  // therefore plays the terrain BGM at scene mount, not at rollDice click.
  // biome-ignore lint/correctness/useExhaustiveDependencies: terrainKey + tileType change track tile entry.
  useEffect(() => {
    const audioMuted = useGameStore.getState().audioMuted;
    if (audioMuted) return;
    const terrainBgm = bgmKeyForTerrain(tileType);
    if (terrainBgm) crossfadeBGM(terrainBgm);
    return () => {
      // Restore era BGM on scene unmount (returning to gameboard view).
      const s = useGameStore.getState();
      if (s.audioMuted) return;
      const eraBgm = bgmKeyForEraRound(s.era, s.round);
      if (eraBgm) crossfadeBGM(eraBgm);
    };
  }, [terrainKey, tileType]);

  // CSS class for warp effects
  const warpInClass = phase === "warp-in" ? ` ${styles.warpIn}` : "";
  const warpOutClass = phase === "warp-out" ? ` ${styles.warpOut}` : "";

  // R21-GE-1: hide scene when ANY tile picker is open.
  // TilePicker mounts inside HexBoardScaler at z:10; scene is position:fixed z:100.
  // Without this, TilePicker renders behind the scene for ALL picker modes (destroy/smog/etc).
  // visibility:hidden preserves all scene state + RAF so interaction resumes seamlessly.
  // Approach B: tilestage always mounts while turnPhase==="landed"; pickers are modal overlays.
  const hideForPicker = tilePickerOpen;

  return (
    <div
      className={`${styles.sceneRoot}${warpInClass}${warpOutClass}`}
      data-testid="sidescroller-scene"
      aria-label={`Sidescroller tile scene: ${tileType}`}
      data-scene-phase={phase}
      style={hideForPicker ? { visibility: "hidden" } : undefined}
    >
      {/* Warp-in flash overlay */}
      {phase === "warp-in" && (
        <div className={styles.warpFlash} data-testid="ss-warp-flash" aria-hidden="true" />
      )}

      {/* LAYER 1 — Sky band */}
      <div className={styles.skyBand} style={skyStyle} aria-hidden="true" />

      {/* LAYER 2 — Single tile background */}
      <div className={styles.tileSlot} data-testid="ss-tile-bg" aria-hidden="true">
        <img
          className={styles.tileBg}
          src={bgSrc}
          alt=""
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div
          className={`${styles.tileBgFallback} ${styles[`fallback_${terrainKey}` as keyof typeof styles] ?? styles.fallback_plains}`}
        />
      </div>

      {/* LAYER 4.5 — Built facility sprite (R4 Bug 2a/2b/4 / R5 #6 anim gate / R5 #12 water Y).
          R5 #6: buildAnimSkipRef — frozen at mount. true = skip construction anim
            (another player landed on an already-built tile). false = run dust/scaffold/reveal.
          R5 #12: per-terrain WATER_GROUND_Y_PCT record replaces old scalar 6.
            Vision-derived: coastline=33%, river=22%.
          Per-facility height via FACILITY_SCREEN_HEIGHT_PCT (R4 Bug 2b).
          Water facilities (floatingsolar/offshoredrilling/tidal) positioned at
          WATER_FACILITY_X_PCT[terrain] from left and WATER_GROUND_Y_PCT[terrain]
          from bottom when terrain is water-eligible (R4 Bug 4). */}
      {builtFacilityId !== undefined &&
        (() => {
          const isWaterFacility = WATER_FACILITY_IDS.has(builtFacilityId);
          const isWaterTerrain = terrainKey in WATER_FACILITY_X_PCT;
          const useWater = isWaterFacility && isWaterTerrain;
          // R5 #12: per-terrain water ground Y derived from vision inspection.
          const waterGroundY = WATER_GROUND_Y_PCT[terrainKey] ?? WATER_GROUND_Y_PCT_DEFAULT;
          const facilityBottomPct = useWater ? waterGroundY : groundY;
          const facilityLeftStyle: React.CSSProperties = useWater
            ? { left: `${WATER_FACILITY_X_PCT[terrainKey]}%`, right: "unset" }
            : {};
          const facilityHeightPct =
            FACILITY_SCREEN_HEIGHT_PCT[builtFacilityId] ?? DEFAULT_FACILITY_HEIGHT_PCT;
          // R5 #6: use mount-time ref — not live prop — so the building player
          // sees the anim even after buildFacility marks the set (same mount cycle).
          const skipAnim = buildAnimSkipRef.current;
          const animClass = skipAnim ? "" : ` ${styles.facilityConstructing}`;
          return (
            <div
              className={`${styles.facilityLayer}${animClass}`}
              style={{ bottom: `${facilityBottomPct}%`, ...facilityLeftStyle }}
              data-testid="ss-facility-built"
              data-facility-id={builtFacilityId}
              aria-label={`Built facility: ${builtFacilityId}`}
              aria-hidden="true"
            >
              {/* Dust puff / scaffolding only during construction sequence */}
              {!skipAnim && (
                <>
                  {/* Dust puff — 0.0-0.5s radial gradient expanding then fading */}
                  <div className={styles.facilityDust} aria-hidden="true" />
                  {/* Scaffolding frame — 0.3-1.0s vertical lines rising from ground */}
                  <div className={styles.facilityScaffolding} aria-hidden="true" />
                </>
              )}
              {/* Facility sprite — scale-in reveal on first build, settled on re-land */}
              <img
                src={`/images/facilities/facility_${builtFacilityId}.png`}
                alt={builtFacilityId}
                className={
                  skipAnim
                    ? styles.facilitySprite
                    : `${styles.facilitySprite} ${styles.facilityBuildReveal}`
                }
                style={{ height: `${facilityHeightPct}vh` }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          );
        })()}

      {/* LAYER 5 — NPCs */}
      <div className={styles.npcLayer} data-testid="ss-npc-layer">
        {npcs.map((npc) => {
          // R7 #9: NPC positions are now PERCENT of viewport so they stay
          // relative when the user resizes / splits the screen. Translate left
          // by half the sprite width to center the anchor on npc.x.
          const npcLeftPct = `${npc.x * 100}%`;
          const npcFaceLeft = npc.file !== "inmate.png"; // inmate faces right
          const isNear = proximateNpc === npc.testId;
          const npcWidthPx = NPC_BASE_W * getNpcScale(npc.file);
          return (
            <button
              key={npc.testId}
              type="button"
              className={styles.npcWrapper}
              style={
                {
                  left: npcLeftPct,
                  marginLeft: `-${Math.round(npcWidthPx / 2)}px`,
                  bottom: `${groundY}%`,
                  transform: npcFaceLeft ? "scaleX(-1)" : "none",
                  "--bubble-flip": npcFaceLeft ? -1 : 1,
                } as React.CSSProperties
              }
              data-testid={npc.testId}
              onClick={() => {
                if (phase !== "active") return;
                const state = useGameStore.getState();
                const tileIdxNow = state.playerTiles[state.currentSeat] ?? 0;
                const isBuilder = ALWAYS_REINTERACTABLE.has(npc.file);
                const key = state.getNpcInteractionKey(tileIdxNow, npc.file);
                const alreadyDone = state.npcInteractionState[key] === "completed";
                if (alreadyDone && !isBuilder) return;
                triggerGreet(npc.file);
                triggerNpcInteract(npc);
                // R6 #1: marking happens inside completion actions, not here.
              }}
            >
              {/* Speech bubble — R3 Bug 9: post-line shown when this NPC's
                  interaction is already completed on this tile.
                  R18-FC-4: marginTop offset for shorter NPCs (e.g. scout-e1.png). */}
              <div
                className={styles.speechBubble}
                data-testid={`${npc.testId}-bubble`}
                style={
                  (NPC_DIALOG_Y_OFFSET[npc.file] ?? 0) !== 0
                    ? { marginTop: `${NPC_DIALOG_Y_OFFSET[npc.file] ?? 0}px` }
                    : undefined
                }
              >
                {(() => {
                  const key = getNpcInteractionKey(currentTileIdxLive, npc.file);
                  const done = npcInteractionState[key] === "completed";
                  return done
                    ? (NPC_COMPLETED_SPEECH[npc.file] ?? NPC_SPEECH[npc.file] ?? "...")
                    : (NPC_SPEECH[npc.file] ?? "...");
                })()}
              </div>

              {/* "Press E" proximity hint */}
              {isNear && (
                <div
                  className={styles.pressEHint}
                  data-testid={`${npc.testId}-press-e`}
                  aria-live="polite"
                >
                  Press E
                </div>
              )}

              {/* WEB.4.SS.5.PT3: NPC sprite extracts have inconsistent crops
                  (several characters lost limbs to flood-fill). Until user
                  hand-crops, force the silhouette placeholder. When
                  USE_NPC_PLACEHOLDER flips false, the <img> path returns and
                  fallback only shows on load error. */}
              {USE_NPC_PLACEHOLDER ? (
                <div
                  className={styles.npcPlaceholder}
                  data-testid={`${npc.testId}-placeholder`}
                  aria-label={npc.label}
                >
                  <div className={styles.npcPlaceholderHead} aria-hidden="true" />
                  <div className={styles.npcPlaceholderBody} aria-hidden="true" />
                  <div className={styles.npcPlaceholderLabel}>{npc.label}</div>
                </div>
              ) : (
                <>
                  <img
                    src={(() => {
                      // R13: scout-e1.png now has its own dedicated sprite.
                      const frame = greetingNpcFile === npc.file ? "greet" : "idle";
                      return `/sprites/npcs-frames/${npc.file.replace(".png", "")}-${frame}-0.png`;
                    })()}
                    alt={npc.label}
                    className={styles.npcSprite}
                    data-testid={`${npc.testId}-sprite`}
                    style={{
                      width: NPC_BASE_W * getNpcScale(npc.file),
                      height: NPC_BASE_H * getNpcScale(npc.file),
                    }}
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.style.display = "none";
                      const fb = el.parentElement?.querySelector(
                        "[data-npc-fallback]",
                      ) as HTMLElement | null;
                      if (fb) fb.style.display = "flex";
                    }}
                  />
                  <div
                    className={styles.npcFallback}
                    style={{ display: "none" }}
                    data-npc-fallback="1"
                    aria-hidden="true"
                  >
                    {npc.label.slice(0, 3).toUpperCase()}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* LAYER 6 — Player character sprite (R23-GH).
          Archetype sprite replaces the teardrop marker. 2-frame animation:
          idle when still, walk when moving. 250ms/frame (4fps pixel-art pace).
          P# chip retained below sprite for hot-seat identification. */}
      {(() => {
        const spriteState = isMoving ? "walk" : "idle";
        const spriteFrame = Math.floor(animTime / 250) % 2;
        const spriteSrc = `/sprites/players/${archetype}-${spriteState}-${spriteFrame}.png`;
        // Hide player during warp-in OR when scenePos hasn't yet been reset to
        // walk-in start. scenePos is seeded at SPAWN_CENTER_X on landing; without
        // this gate the sprite flashes at the walk-in END position for ~400ms
        // before walking-in resets x=-PLAYER_SIZE.
        const hidePlayerDuringWarp =
          phase === "warp-in" || (phase === "walking-in" && currentX >= SPAWN_CENTER_X);
        return (
          <div
            className={`${styles.playerChar}${isMoving ? ` ${styles.playerWalking}` : ""}`}
            style={{
              transform: playerTransform,
              bottom: `${groundY}%`,
              left: playerLeftPct,
              visibility: hidePlayerDuringWarp ? "hidden" : "visible",
            }}
            data-testid={`ss-player-p${currentSeat + 1}`}
            aria-label={`Player ${currentSeat + 1}`}
          >
            {/* Archetype sprite — pixel-art, crisp scaling */}
            <img
              src={spriteSrc}
              alt={`Player ${currentSeat + 1} (${archetype})`}
              className={styles.playerSprite}
              data-testid={`ss-player-sprite-p${currentSeat + 1}`}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            {/* P# chip — counter-flip so label reads forward regardless of walk dir.
                Kept for hot-seat clarity in 4-player mode. */}
            <div
              className={styles.playerMarker}
              style={{ background: seatColor }}
              data-testid={`ss-player-marker-p${currentSeat + 1}`}
              aria-hidden="true"
            >
              <span
                className={styles.playerMarkerLabel}
                style={{ display: "inline-block", transform: `scaleX(${dirRef.current})` }}
              >
                P{currentSeat + 1}
              </span>
            </div>
          </div>
        );
      })()}

      {/* M-1 SDG Quiz modal — mounts when pendingQuiz is non-null.
          Gated by boardAnimationActive inside QuizModal (R11.2 invariant). */}
      <QuizModal />

      {/* T-1 Scout modal — mounts when pendingScout is non-null.
          Gated by boardAnimationActive inside ScoutModal (R11.2 invariant). */}
      <ScoutModal />

      {/* R5 #2b: HandPanel removed — lives persistently in App.tsx right column. */}
    </div>
  );
}
